import { ShieldCheck } from 'lucide-react'

import { SEO } from './SEO'

type LegalPageProps = {
  kind: 'privacy' | 'terms'
}

const updated = 'August 6, 2026'

const privacy = {
  title: 'Privacy Policy',
  intro: 'AgentWhisper is built to keep dictation on your Mac. This policy explains the limited information involved when you buy, download, or get support for AgentWhisper.',
  sections: [
    ['What stays on your Mac', 'Your audio, local Whisper transcription, and local app history stay on your device. AgentWhisper does not upload your microphone audio or transcriptions to our servers.'],
    ['Information we process', 'We process the account email you provide, purchase and license status, and messages you send to support. Stripe processes payment details. We do not receive or store your complete card number.'],
    ['Optional AI providers', 'If you choose AI cleanup and enter a provider key, requests are sent directly from your Mac to the provider you select. Their privacy policy applies to those requests.'],
    ['How we use information', 'We use account and purchase information to provide downloads, verify your lifetime license, respond to support, and keep the service secure.'],
    ['Your choices', 'You may request account deletion or ask a privacy question by emailing altuzar@gmail.com.'],
  ],
}

const terms = {
  title: 'Terms of Service',
  intro: 'These terms apply when you purchase, download, or use AgentWhisper.',
  sections: [
    ['License', 'A lifetime purchase gives one person a non-transferable license to use AgentWhisper on their own Macs, subject to these terms.'],
    ['Your responsibility', 'You are responsible for the content you dictate and for any optional AI-provider accounts and keys you choose to use. Do not use AgentWhisper to violate applicable law or others’ rights.'],
    ['Updates and availability', 'Your purchase includes the AgentWhisper 1.x updates described at purchase. We may improve, modify, or discontinue features when reasonably necessary.'],
    ['Payments and refunds', 'Payments are processed by Stripe. Where required by law, you retain applicable consumer rights. For help with a purchase, contact altuzar@gmail.com.'],
    ['Disclaimer', 'AgentWhisper is provided as available. To the maximum extent allowed by law, we disclaim warranties not expressly stated in these terms.'],
  ],
}

export function LegalPage({ kind }: LegalPageProps) {
  const content = kind === 'privacy' ? privacy : terms

  return (
    <>
      <SEO
        title={`${content.title} | AgentWhisper`}
        description={content.intro}
        url={`https://agentwhisper.altuzar.dev/${kind}`}
      />
      <main className="min-h-screen bg-bg px-6 py-10 text-fg sm:px-10">
        <div className="mx-auto max-w-3xl">
          <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-purple hover:text-fg">
            <ShieldCheck className="h-4 w-4" /> AgentWhisper
          </a>
          <article className="mt-10 rounded-brand-lg border border-white/10 bg-surface p-7 sm:p-10">
            <p className="text-sm font-medium text-fg-muted">Last updated: {updated}</p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight">{content.title}</h1>
            <p className="mt-6 text-lg leading-8 text-fg-dim">{content.intro}</p>
            <div className="mt-10 space-y-8">
              {content.sections.map(([heading, body]) => (
                <section key={heading}>
                  <h2 className="text-xl font-semibold">{heading}</h2>
                  <p className="mt-2 leading-7 text-fg-dim">{body}</p>
                </section>
              ))}
            </div>
            <p className="mt-10 border-t border-white/10 pt-6 text-sm text-fg-muted">
              Questions? <a className="text-brand-purple hover:text-fg" href="mailto:altuzar@gmail.com">Email support</a>.
            </p>
          </article>
        </div>
      </main>
    </>
  )
}
